// Knowledge graph visualization component - 180 lines ✅
import { useCallback, useMemo, useState } from 'react'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  NodeTypes,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
  MarkerType,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { KGNode, KGEdge } from '@/hooks/useKnowledgeGraph'
import { KGNodeComponent } from './KGNodeComponent'

interface KnowledgeGraphViewProps {
  nodes: KGNode[]
  edges: KGEdge[]
  onNodeClick: (node: KGNode) => void
}

const nodeTypes: NodeTypes = {
  custom: KGNodeComponent,
}

// Node colors by entity type
const NODE_COLORS = {
  issue: '#FF6B6B',
  knowledge_entry: '#4ECDC4',
  resolution_method: '#95E1D3',
}

// Convert KGNode to ReactFlow Node
function convertToFlowNode(node: KGNode, index: number): Node {
  const color = NODE_COLORS[node.entity_type] || '#CCCCCC'

  return {
    id: node.id,
    type: 'custom',
    position: {
      // Initial layout in a circle
      x: 400 + 300 * Math.cos((index * 2 * Math.PI) / 20),
      y: 300 + 300 * Math.sin((index * 2 * Math.PI) / 20),
    },
    data: {
      label: node.label,
      entityType: node.entity_type,
      properties: node.properties,
      kgNode: node,
    },
    style: {
      background: color,
      color: 'white',
      border: '2px solid #003C41',
      borderRadius: '8px',
      padding: '10px',
      fontSize: '12px',
      fontWeight: 'bold',
    },
  }
}

// Convert KGEdge to ReactFlow Edge
function convertToFlowEdge(edge: KGEdge): Edge {
  const edgeColor = edge.relationship_type === 'resolved_by' ? '#4ECDC4' : '#95E1D3'

  return {
    id: edge.id,
    source: edge.source_node_id,
    target: edge.target_node_id,
    type: 'smoothstep',
    animated: edge.relationship_type === 'resolved_by',
    label: edge.relationship_type.replace(/_/g, ' '),
    style: {
      stroke: edgeColor,
      strokeWidth: Math.max(1, edge.weight * 3),
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edgeColor,
    },
  }
}

export function KnowledgeGraphView({ nodes, edges, onNodeClick }: KnowledgeGraphViewProps) {
  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([])
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  // Convert data to ReactFlow format
  useMemo(() => {
    const convertedNodes = nodes.map((node, index) => convertToFlowNode(node, index))
    const convertedEdges = edges.map(edge => convertToFlowEdge(edge))

    setFlowNodes(convertedNodes)
    setFlowEdges(convertedEdges)
  }, [nodes, edges, setFlowNodes, setFlowEdges])

  // Handle node click
  const onNodeClickHandler = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const kgNode = nodes.find(n => n.id === node.id)
      if (kgNode) {
        setSelectedNodeId(node.id)
        onNodeClick(kgNode)
      }
    },
    [nodes, onNodeClick]
  )

  // Update node styles when selected
  useMemo(() => {
    setFlowNodes(prevNodes =>
      prevNodes.map(node => ({
        ...node,
        style: {
          ...node.style,
          border: node.id === selectedNodeId ? '3px solid #003C41' : '2px solid #003C41',
          boxShadow: node.id === selectedNodeId ? '0 0 10px rgba(0, 60, 65, 0.5)' : 'none',
        },
      }))
    )
  }, [selectedNodeId, setFlowNodes])

  return (
    <div className="h-[600px] w-full rounded-lg border border-kaycoo-teal-dark bg-white">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClickHandler}
        nodeTypes={nodeTypes}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        attributionPosition="bottom-left"
      >
        <Background color="#e5e7eb" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const entityType = node.data?.entityType as keyof typeof NODE_COLORS
            return NODE_COLORS[entityType] || '#CCCCCC'
          }}
          maskColor="rgba(0, 60, 65, 0.1)"
        />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 rounded-lg border border-kaycoo-teal-dark bg-white p-3 shadow-lg">
        <h3 className="mb-2 text-xs font-semibold text-kaycoo-teal-dark">Node Types</h3>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded" style={{ background: NODE_COLORS.issue }} />
            <span className="text-kaycoo-charcoal-2">Issues</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded" style={{ background: NODE_COLORS.knowledge_entry }} />
            <span className="text-kaycoo-charcoal-2">Knowledge Entries</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded" style={{ background: NODE_COLORS.resolution_method }} />
            <span className="text-kaycoo-charcoal-2">Resolutions</span>
          </div>
        </div>
      </div>
    </div>
  )
}
